import { useState } from 'react'

import type { ShopMedia, ShopMediaKind } from '@/lib/api/shop-media-api'
import { MediaBrowser } from '@/components/media/media-browser'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { m } from '@/lib/paraglide/messages'

interface Props {
    open: boolean
    onOpenChange: (open: boolean) => void
    /** Chọn nhiều file (mặc định một). */
    multiple?: boolean
    /** Chỉ hiện ảnh hoặc video. */
    kind?: ShopMediaKind
    onSelect: (items: ShopMedia[]) => void
    title?: string
}

/** Câu hướng dẫn theo loại file đang chọn (video thì nói video, không nói ảnh). */
function pickerHint(kind: ShopMediaKind | undefined, multiple: boolean) {
    if (kind === 'video')
        return multiple
            ? m.media_picker_hint_multiple_video()
            : m.media_picker_hint_single_video()
    return multiple
        ? m.media_picker_hint_multiple()
        : m.media_picker_hint_single()
}

/**
 * Hộp chọn ảnh từ thư viện (dùng chung cho ô ảnh, trình soạn nội dung…):
 * tìm, lọc, tải lên ngay trong hộp. Chọn một → trả luôn; chọn nhiều → bấm
 * "Chọn". File vừa tải lên được chọn sẵn.
 */
export function MediaPicker({
    open,
    onOpenChange,
    multiple = false,
    kind,
    onSelect,
    title,
}: Props) {
    const [picked, setPicked] = useState<ShopMedia[]>([])
    const [emptyError, setEmptyError] = useState(false)
    const close = (next: boolean) => {
        if (!next) {
            setPicked([])
            setEmptyError(false)
        }
        onOpenChange(next)
    }
    const finish = (items: ShopMedia[]) => {
        onSelect(items)
        close(false)
    }
    const toggle = (item: ShopMedia) => {
        if (!multiple) return finish([item])
        setPicked((cur) =>
            cur.some((x) => x.id === item.id)
                ? cur.filter((x) => x.id !== item.id)
                : [...cur, item],
        )
    }
    const uploaded = (items: ShopMedia[]) => {
        if (!multiple) return finish(items.slice(0, 1))
        setPicked((cur) => [...cur, ...items])
    }

    return (
        <Dialog open={open} onOpenChange={close}>
            <DialogContent className="flex max-h-[90vh] flex-col sm:max-w-4xl">
                <DialogHeader>
                    <DialogTitle>{title ?? m.media_picker_title()}</DialogTitle>
                    <DialogDescription>
                        {pickerHint(kind, multiple)}
                    </DialogDescription>
                </DialogHeader>
                <div className="-mx-1 min-h-0 flex-1 overflow-y-auto px-1 py-1">
                    {open && (
                        <MediaBrowser
                            kind={kind}
                            selected={new Set(picked.map((x) => x.id))}
                            onPick={toggle}
                            onUploaded={uploaded}
                        />
                    )}
                </div>
                {multiple && (
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => close(false)}
                        >
                            {m.media_cancel()}
                        </Button>
                        <Button
                            type="button"
                            onClick={() =>
                                picked.length
                                    ? finish(picked)
                                    : setEmptyError(true)
                            }
                        >
                            {m.media_pick_count({ count: picked.length })}
                        </Button>
                    </DialogFooter>
                )}
                {multiple && emptyError && picked.length === 0 && (
                    <p className="text-sm text-destructive" role="alert">
                        {m.media_pick_none()}
                    </p>
                )}
            </DialogContent>
        </Dialog>
    )
}
